import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Input,
  Button,
  Label,
  FormGroup,
  UncontrolledTooltip,
} from "reactstrap";

import { ApiContext } from "../../contexts/ApiContext";
import { useNotification } from "../../contexts/NotificationContext";
import AccountService from "../../service/account.service";
import { formatCentsToBRL, parseBRLToCents } from "../../utils/currency";

const emptyAddress = {
  street: "",
  number: "",
  neighborhood: "",
  city: "",
  state: "",
  zipCode: "",
  latitude: null,
  longitude: null,
};

const HelpLabel = ({ id, children, tip }) => (
  <Label className="d-flex align-items-center" style={{ gap: 6 }}>
    <span>{children}</span>
    <i
      id={id}
      className="tim-icons icon-alert-circle-exc text-muted"
      style={{ fontSize: 14, cursor: "help" }}
    />
    <UncontrolledTooltip target={id} placement="top">
      {tip}
    </UncontrolledTooltip>
  </Label>
);

export const DeliverySettingsForm = ({ onSaved, showSaveButton = true }) => {
  const { account, user, getUser } = useContext(ApiContext);
  const { notify } = useNotification();

  const [address, setAddress] = useState(emptyAddress);
  const [hasAddress, setHasAddress] = useState(false);
  const [geocoded, setGeocoded] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);

  const [feeMode, setFeeMode] = useState("FIXED");
  const [fixedCents, setFixedCents] = useState(500);
  const [fixedInput, setFixedInput] = useState(formatCentsToBRL(500));
  const [baseCents, setBaseCents] = useState(500);
  const [baseInput, setBaseInput] = useState(formatCentsToBRL(500));
  const [baseKm, setBaseKm] = useState("3");
  const [extraCents, setExtraCents] = useState(180);
  const [extraInput, setExtraInput] = useState(formatCentsToBRL(180));
  const [maxKm, setMaxKm] = useState("");

  const [neighborhoods, setNeighborhoods] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const [manualNeighborhood, setManualNeighborhood] = useState("");
  const [manualFeeInput, setManualFeeInput] = useState(formatCentsToBRL(500));
  const [manualFeeCents, setManualFeeCents] = useState(500);

  const [saving, setSaving] = useState(false);

  const loadNeighborhoodData = async (accountId) => {
    try {
      const [items, tips] = await Promise.all([
        AccountService.getDeliveryNeighborhoods(accountId),
        AccountService.getDeliveryNeighborhoodSuggestions(accountId),
      ]);
      setNeighborhoods(
        (items || []).map((n) => ({
          ...n,
          feeInput: formatCentsToBRL(n.feeCents),
        })),
      );
      setSuggestions(tips || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!account?.id) return;

    const fixed = account.deliveryFixedCents ?? 500;
    const base = account.deliveryBaseCents ?? 500;
    const extra = account.deliveryExtraPerKmCents ?? 180;

    setFeeMode(account.deliveryFeeMode || "FIXED");
    setFixedCents(fixed);
    setFixedInput(formatCentsToBRL(fixed));
    setBaseCents(base);
    setBaseInput(formatCentsToBRL(base));
    setBaseKm(account.deliveryBaseKm != null ? String(account.deliveryBaseKm) : "3");
    setExtraCents(extra);
    setExtraInput(formatCentsToBRL(extra));
    setMaxKm(account.deliveryMaxKm != null ? String(account.deliveryMaxKm) : "");

    AccountService.getAddress(account.id)
      .then((data) => {
        if (!data) return;
        setHasAddress(true);
        setAddress({
          street: data.street || "",
          number: data.number || "",
          neighborhood: data.neighborhood || "",
          city: data.city || "",
          state: data.state || "",
          zipCode: data.zipCode || "",
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
        });
        setGeocoded(!!(data.latitude && data.longitude));
      })
      .catch(() => {
        setHasAddress(false);
        setAddress(emptyAddress);
        setGeocoded(false);
      });

    loadNeighborhoodData(account.id);
  }, [account]);

  const filteredSuggestions = useMemo(() => {
    const q = suggestionQuery.trim().toLowerCase();
    const configured = new Set(neighborhoods.map((n) => (n.normalizedName || n.name || "").toLowerCase()));
    return suggestions
      .filter((s) => !configured.has((s.normalizedName || s.name || "").toLowerCase()))
      .filter((s) => !q || (s.name || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [suggestions, neighborhoods, suggestionQuery]);

  const handleCepChange = async (value) => {
    const cleanCep = value.replace(/\D/g, "");
    let formattedCep = cleanCep;
    if (cleanCep.length > 5) {
      formattedCep = cleanCep.slice(0, 5) + "-" + cleanCep.slice(5, 8);
    }
    setAddress((prev) => ({ ...prev, zipCode: formattedCep, latitude: null, longitude: null }));
    setGeocoded(false);

    if (cleanCep.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      if (data.erro) {
        notify("tr", "warning", "CEP não encontrado", "icon-alert-circle-exc");
        return;
      }
      setAddress((prev) => ({
        ...prev,
        street: data.logradouro || prev.street,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state,
        zipCode: formattedCep,
        latitude: null,
        longitude: null,
      }));
      setGeocoded(false);
    } catch (err) {
      console.error(err);
      notify("tr", "danger", "Erro ao buscar CEP", "icon-alert-circle-exc");
    } finally {
      setLoadingCep(false);
    }
  };

  const handleMoneyChange = (raw, setCents, setInput) => {
    const cents = parseBRLToCents(raw);
    setCents(cents);
    setInput(formatCentsToBRL(cents));
  };

  const exampleFee = () => {
    const km = parseFloat(String(baseKm).replace(",", ".")) || 3;
    const sampleKm = km + 2;
    const fee =
      sampleKm <= km
        ? baseCents
        : baseCents + Math.ceil(sampleKm - km) * extraCents;
    return { km, sampleKm, fee };
  };

  const addNeighborhood = (name, feeCents = 500, city = "", state = "") => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    const exists = neighborhoods.some(
      (n) => n.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      notify("tr", "warning", "Bairro já está na lista", "icon-alert-circle-exc");
      return;
    }
    setNeighborhoods((prev) => [
      ...prev,
      {
        name: trimmed,
        feeCents,
        feeInput: formatCentsToBRL(feeCents),
        active: true,
        city: city || address.city || "",
        state: state || address.state || "",
      },
    ]);
  };

  const handleSave = async () => {
    if (!account?.id) return;

    if (!address.street || !address.number || !address.neighborhood || !address.city || !address.state || !address.zipCode) {
      notify("tr", "warning", "Preencha o endereço completo da loja", "icon-alert-circle-exc");
      return;
    }

    if (feeMode === "NEIGHBORHOOD" && neighborhoods.length === 0) {
      notify("tr", "warning", "Adicione ao menos um bairro com taxa", "icon-alert-circle-exc");
      return;
    }

    setSaving(true);
    try {
      await AccountService.upsertAddress(account.id, {
        street: address.street.trim(),
        number: address.number.trim(),
        neighborhood: address.neighborhood.trim(),
        city: address.city.trim(),
        state: address.state.trim().toUpperCase(),
        zipCode: address.zipCode.trim(),
      });

      await AccountService.update(account.id, {
        deliveryFeeMode: feeMode,
        deliveryFixedCents: feeMode === "FIXED" ? fixedCents : null,
        deliveryBaseCents: feeMode === "DISTANCE" ? baseCents : null,
        deliveryBaseKm: feeMode === "DISTANCE" ? parseFloat(String(baseKm).replace(",", ".")) || null : null,
        deliveryExtraPerKmCents: feeMode === "DISTANCE" ? extraCents : null,
        deliveryMaxKm:
          feeMode === "DISTANCE" && maxKm !== ""
            ? parseFloat(String(maxKm).replace(",", ".")) || null
            : null,
      });

      if (feeMode === "NEIGHBORHOOD") {
        await AccountService.upsertDeliveryNeighborhoods(
          account.id,
          neighborhoods.map((n) => ({
            id: n.id,
            name: n.name,
            feeCents: n.feeCents,
            active: n.active !== false,
            city: n.city || address.city,
            state: n.state || address.state,
          })),
        );
      }

      notify("tr", "success", "Configurações de entrega salvas!", "icon-check-2");
      setHasAddress(true);
      setGeocoded(false);
      setTimeout(async () => {
        try {
          const data = await AccountService.getAddress(account.id);
          setGeocoded(!!(data?.latitude && data?.longitude));
        } catch {
          // ignore
        }
      }, 2500);

      await loadNeighborhoodData(account.id);
      if (user?.id) await getUser(user.id);
      onSaved?.();
    } catch (err) {
      console.error(err);
      notify("tr", "danger", "Erro ao salvar configurações de entrega", "icon-alert-circle-exc");
    } finally {
      setSaving(false);
    }
  };

  const { km: exKm, sampleKm, fee: exFee } = exampleFee();

  return (
    <>
      <p className="text-muted" style={{ fontSize: 14, marginBottom: 20 }}>
        O endereço da loja é usado como ponto de partida para calcular a distância até o cliente no cardápio digital.
      </p>

      <h6 className="mb-3">Endereço do estabelecimento</h6>
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Preencha pelo CEP. As coordenadas são geradas ao salvar (necessárias para frete por distância).
      </p>

      {hasAddress && (
        <p className="text-muted" style={{ fontSize: 13 }}>
          Coordenadas:{" "}
          {geocoded ? (
            <span style={{ color: "#2dce89" }}>prontas para calcular frete</span>
          ) : (
            <span style={{ color: "#fb6340" }}>processando… aguarde alguns segundos após salvar</span>
          )}
        </p>
      )}
      <Row>
        <Col md="3">
          <FormGroup>
            <Label>CEP *</Label>
            <Input
              value={address.zipCode}
              onChange={(e) => handleCepChange(e.target.value)}
              placeholder="00000-000"
              disabled={loadingCep}
            />
          </FormGroup>
        </Col>
        <Col md="7">
          <FormGroup>
            <Label>Rua *</Label>
            <Input
              value={address.street}
              onChange={(e) =>
                setAddress({ ...address, street: e.target.value, latitude: null, longitude: null })
              }
            />
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <Label>Número *</Label>
            <Input
              value={address.number}
              onChange={(e) => setAddress({ ...address, number: e.target.value })}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label>Bairro *</Label>
            <Input
              value={address.neighborhood}
              onChange={(e) =>
                setAddress({ ...address, neighborhood: e.target.value, latitude: null, longitude: null })
              }
            />
          </FormGroup>
        </Col>
        <Col md="5">
          <FormGroup>
            <Label>Cidade *</Label>
            <Input
              value={address.city}
              onChange={(e) =>
                setAddress({ ...address, city: e.target.value, latitude: null, longitude: null })
              }
            />
          </FormGroup>
        </Col>
        <Col md="3">
          <FormGroup>
            <Label>UF *</Label>
            <Input
              value={address.state}
              maxLength={2}
              onChange={(e) =>
                setAddress({
                  ...address,
                  state: e.target.value.toUpperCase(),
                  latitude: null,
                  longitude: null,
                })
              }
            />
          </FormGroup>
        </Col>
      </Row>

      <hr />
      <h6 className="mb-2">Como cobrar a entrega no cardápio</h6>
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Escolha se o cliente paga valor fixo, por distância ou conforme o bairro configurado.
      </p>

      <div className="mb-3">
        {[
          { value: "FIXED", label: "Taxa fixa", desc: "mesmo valor para qualquer endereço" },
          {
            value: "DISTANCE",
            label: "Por distância",
            desc: "calculado automaticamente no checkout",
            requiresDistanceFeature: true,
          },
          {
            value: "NEIGHBORHOOD",
            label: "Por bairro",
            desc: "cada bairro tem sua taxa (bairros não cadastrados ficam fora da área)",
          },
        ].map((opt) => {
          const locked =
            opt.requiresDistanceFeature && account?.features?.deliveryDistance === false;
          return (
          <label
            key={opt.value}
            className="d-flex align-items-start mb-2"
            style={{
              gap: 10,
              cursor: locked ? "not-allowed" : "pointer",
              opacity: locked ? 0.55 : 1,
            }}
          >
            <input
              type="radio"
              name="feeMode"
              checked={feeMode === opt.value}
              disabled={locked}
              onChange={() => setFeeMode(opt.value)}
              style={{ marginTop: 4 }}
            />
            <span>
              <strong>{opt.label}</strong>
              <span className="text-muted"> — {opt.desc}</span>
              {locked && (
                <span className="text-muted d-block" style={{ fontSize: 12 }}>
                  Indisponível no plano atual
                </span>
              )}
            </span>
          </label>
          );
        })}
      </div>

      {feeMode === "FIXED" && (
        <Row>
          <Col md="4">
            <FormGroup>
              <HelpLabel id="tip-fixed-fee" tip="Valor único cobrado em todo pedido de entrega, independente do endereço do cliente.">
                Valor da taxa
              </HelpLabel>
              <Input
                type="text"
                value={fixedInput}
                onChange={(e) => handleMoneyChange(e.target.value, setFixedCents, setFixedInput)}
                placeholder="R$ 0,00"
              />
            </FormGroup>
          </Col>
        </Row>
      )}

      {feeMode === "DISTANCE" && (
        <>
          <Row>
            <Col md="3">
              <FormGroup>
                <HelpLabel id="tip-base-fee" tip="Taxa cobrada quando a entrega está dentro da distância inclusa (em km).">
                  Valor base
                </HelpLabel>
                <Input
                  type="text"
                  value={baseInput}
                  onChange={(e) => handleMoneyChange(e.target.value, setBaseCents, setBaseInput)}
                  placeholder="R$ 0,00"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <HelpLabel id="tip-base-km" tip="Distância em km incluída no valor base. Acima disso, cobra-se o valor por km extra.">
                  Km inclusos
                </HelpLabel>
                <Input value={baseKm} onChange={(e) => setBaseKm(e.target.value)} placeholder="3" />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <HelpLabel id="tip-extra-km" tip="Valor adicionado para cada km acima dos km inclusos. Ex.: 2 km extras × R$ 1,80 = + R$ 3,60.">
                  Valor por km extra
                </HelpLabel>
                <Input
                  type="text"
                  value={extraInput}
                  onChange={(e) => handleMoneyChange(e.target.value, setExtraCents, setExtraInput)}
                  placeholder="R$ 0,00"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <HelpLabel id="tip-max-km" tip="Limite máximo de entrega. Clientes fora dessa distância não conseguem finalizar o pedido. Deixe vazio para sem limite.">
                  Raio máximo (km)
                </HelpLabel>
                <Input value={maxKm} onChange={(e) => setMaxKm(e.target.value)} placeholder="Opcional" />
              </FormGroup>
            </Col>
          </Row>
          <div
            style={{
              background: "#f6f9fc",
              borderRadius: 8,
              padding: "12px 14px",
              fontSize: 13,
              color: "#525f7f",
            }}
          >
            <strong>Exemplo com os valores acima:</strong>
            <ul style={{ marginBottom: 0, paddingLeft: 18, marginTop: 8 }}>
              <li>
                Até {exKm} km → {formatCentsToBRL(baseCents)}
              </li>
              <li>
                {sampleKm} km → {formatCentsToBRL(exFee)} (base + km extras)
              </li>
            </ul>
          </div>
        </>
      )}

      {feeMode === "NEIGHBORHOOD" && (
        <>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Cadastre os bairros atendidos e a taxa de cada um. Sugestões vêm de pedidos anteriores nesta conta.
          </p>

          <FormGroup className="mb-3">
            <Label>Adicionar bairro</Label>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Input
                value={manualNeighborhood}
                onChange={(e) => setManualNeighborhood(e.target.value)}
                placeholder="Ex.: Centro"
              />
              <Input
                style={{ maxWidth: 140 }}
                value={manualFeeInput}
                onChange={(e) =>
                  handleMoneyChange(e.target.value, setManualFeeCents, setManualFeeInput)
                }
              />
              <Button
                color="primary"
                outline
                className="flex-shrink-0 m-0"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => {
                  addNeighborhood(manualNeighborhood, manualFeeCents);
                  setManualNeighborhood("");
                }}
              >
                Adicionar
              </Button>
            </div>
          </FormGroup>

          {filteredSuggestions.length > 0 && (
            <FormGroup className="mb-3">
              <Label>Bairros de pedidos anteriores</Label>
              <Input
                className="mb-2"
                value={suggestionQuery}
                onChange={(e) => setSuggestionQuery(e.target.value)}
                placeholder="Filtrar..."
              />
              <div style={{ maxHeight: 160, overflowY: "auto", overflowX: "hidden", border: "1px solid #e9ecef", borderRadius: 6 }}>
                {filteredSuggestions.map((s) => (
                  <button
                    key={`${s.normalizedName}-${s.city}`}
                    type="button"
                    className="btn btn-link btn-sm text-left d-block w-100"
                    style={{ borderBottom: "1px solid #f0f0f0", whiteSpace: "normal", wordBreak: "break-word" }}
                    onClick={() => addNeighborhood(s.name, 500, s.city, s.state)}
                  >
                    {s.name}
                    {s.city ? ` — ${s.city}/${s.state}` : ""}
                  </button>
                ))}
              </div>
            </FormGroup>
          )}

          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Bairro</th>
                  <th style={{ width: 160 }}>Taxa</th>
                  <th style={{ width: 90, textAlign: "center" }}>Ativo</th>
                  <th style={{ width: 70 }} />
                </tr>
              </thead>
              <tbody>
                {neighborhoods.map((n, idx) => (
                  <tr key={n.id || `${n.name}-${idx}`}>
                    <td style={{ verticalAlign: "middle" }}>{n.name}</td>
                    <td>
                      <Input
                        type="text"
                        value={n.feeInput}
                        onChange={(e) => {
                          const cents = parseBRLToCents(e.target.value);
                          setNeighborhoods((prev) =>
                            prev.map((item, i) =>
                              i === idx
                                ? { ...item, feeCents: cents, feeInput: formatCentsToBRL(cents) }
                                : item,
                            ),
                          );
                        }}
                      />
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <input
                        type="checkbox"
                        checked={n.active !== false}
                        onChange={(e) => {
                          const active = e.target.checked;
                          setNeighborhoods((prev) =>
                            prev.map((item, i) => (i === idx ? { ...item, active } : item)),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        onClick={async () => {
                          if (n.id) {
                            try {
                              await AccountService.deleteDeliveryNeighborhood(account.id, n.id);
                            } catch (err) {
                              console.error(err);
                            }
                          }
                          setNeighborhoods((prev) => prev.filter((_, i) => i !== idx));
                        }}
                      >
                        <i className="tim-icons icon-simple-remove" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {neighborhoods.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      Nenhum bairro cadastrado ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showSaveButton && (
        <div className="mt-4">
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Salvando…" : "Salvar entrega"}
          </Button>
        </div>
      )}
    </>
  );
};

const DeliverySettings = () => (
  <div className="content">
    <Row>
      <Col md="12">
        <div className="card">
          <div className="card-header">
            <h5 className="title">Entrega e frete</h5>
          </div>
          <div className="card-body">
            <DeliverySettingsForm />
          </div>
        </div>
      </Col>
    </Row>
  </div>
);

export default DeliverySettings;
